import type { Reduced } from 'ramda'
import ramdaReduce from 'ramda/src/reduce'
import ramdaReduced from 'ramda/src/reduced'
// prefixes included in CyberSource gateway error messages (from Paradox Labs)
const invalidCreditCardPrefixRegex = /^Unable to place order: Invalid credit card/
const transactionFailedPrefixRegex = /^Unable to place order: Transaction Failed:\s*/

export const isCyberSourceGatewayError = (errorMessage: string): boolean =>
  transactionFailedPrefixRegex.test(errorMessage)
export const isCyberSourceValidationError = (errorMessage: string): boolean =>
  invalidCreditCardPrefixRegex.test(errorMessage)

const defaultPaymentErrorMessage =
  'There is a problem completing this transaction. Please review or contact Customer Experience.'

interface Translation {
  pattern: RegExp
  replacement: (matchArray: RegExpMatchArray) => string
}

// translations are applied in order, first match wins
const translations: ReadonlyArray<Translation> = [
  {
    pattern: /cybersource|decision manager/i,
    replacement: () => defaultPaymentErrorMessage,
  },
  {
    pattern: /^Transaction declined\.$/,
    replacement: () => defaultPaymentErrorMessage,
  },
  {
    pattern: /Address verification failed.+$/,
    replacement: () =>
      'There is a problem with the billing information. Please review and try again.',
  },
  {
    pattern:
      /^Credit card issuer declined the request\.|Invalid expiration date\.|Invalid credit card.+$/,
    replacement: ([match]) => `${match} Please edit your payment information.`,
  },
]

const translationsReducer =
  (errorMessage: string) =>
  (
    acc: string | undefined,
    { pattern, replacement }: Translation,
  ): string | undefined | Reduced<string | undefined> => {
    // should not be necessary when using `ramdaReduced` to short-circuit, but let's be complete
    if (acc) return acc
    const matchArray = errorMessage.match(pattern)
    return matchArray ? ramdaReduced(replacement(matchArray)) : undefined
  }

/**
 * Translate message as needed to sanitize or improve for UX
 */
const translateMessageIfNeeded: (errorMessage: string) => string = (errorMessage: string) =>
  ramdaReduce(translationsReducer(errorMessage), undefined)(translations) || errorMessage

export const paymentErrorMessage = (errorMessage: string): string => {
  if (isCyberSourceGatewayError(errorMessage)) {
    return translateMessageIfNeeded(errorMessage.replace(transactionFailedPrefixRegex, ''))
  }
  if (isCyberSourceValidationError(errorMessage)) {
    return translateMessageIfNeeded(errorMessage)
  }
  return defaultPaymentErrorMessage
}
